/**
 * Search
 *
 * This is where the main suggestion behavior is defined.
 * The logic for fetching and caching domain suggestions is
 * somewhat complex for performance reasons, so the steps are
 * documented below.
 *
 */

import * as Bloom from '@mcrowe/minibloom'
import * as Api from './api'
import * as Logger from '../service/logger'
import * as Util from './util'
import * as CachedResults from './cached-results'
import {
  ISearchResult
} from '../types'


export default class Search {

  public id: string
  private all: Set<string> = new Set()
  private cached: string[] = []
  private filter?: Bloom.IFilter
  private numFetches = 0

  constructor(private base: string, private pageSize: number) {
    this.id = Util.makeId(6)
  }

  async fetch(): Promise<ISearchResult> {
    const names = await this.fetchNames()

    this.numFetches += 1

    return {
      names,
      base: this.base,
      searchId: this.id,
      page: this.numFetches
    }
  }

  private async fetchNames(): Promise<string[]> {
    // On the 3rd fetch, we start passing a filter to the server
    // so that we only get back results we haven't seen yet.
    // We don't do this early on because it makes requests slower
    // and wasted results aren't as costly then.
    if (this.numFetches == 2) {
      Logger.debug('Making filter')
      this.makeFilter()
    }

    if (this.numFetches == 0) {
      // If we have a pre-cached result, use it.
      // We need to put them through the cache so that they don't get re-used
      // and we fetch another 2 pages so going to the next page is fast.
      const cachedResults = CachedResults.get(this.base, this.pageSize)
      if (cachedResults) {
        Logger.debug('First fetch: loading from precached results')
        this.addNamesToCache(cachedResults)
        this.cachePages(2, 20)

        return this.popCachedPage()
      }

      Logger.debug('First fetch: loading 2 pages of results')

      // The first fetch
      // Cache 2 pages and use the first
      await this.cachePages(2, 20)
      return this.popCachedPage()

    } else if (this.numCachedPages() >= 2) {

      Logger.debug('There are at least 2 cached pages, using the first')

      // There are at least 2 cached pages
      // Use the first cache page
      return this.popCachedPage()

    } else if (this.numCachedPages() >= 1) {

      Logger.debug('There is at least 1 cached page. Caching 5 more asynchronously')

      // There is at least 1 cached page
      // Cache 5 more pages of names asynchronously
      // And use the cached page immediately
      this.cachePages(5, 25)
      return this.popCachedPage()

    } else {

      Logger.debug('There is less than 1 cached page. Caching 5 more and the returning the first')

      // There is less than 1 cached page
      // Cache 5 more pages of names
      // And then use the first cached page
      await this.cachePages(5, 25)
      return this.popCachedPage()

    }

  }

  private async cachePages(n: number, samplePages: number) {
    const serializedFilter = this.serializeFilter()

    const names = await Api.suggest({
      n: n * this.pageSize,
      sample: samplePages * this.pageSize,
      base: this.base,
      filter: serializedFilter
    })

    this.addNamesToCache(names)
  }

  private addNamesToCache(names: string[]) {
    for (const name of names) {
      if (!this.all.has(name)) {
        this.cached.push(name)
        this.all.add(name)
        if (this.filter) {
          this.filter.add(name)
        }
      }
    }
  }

  private makeFilter() {
    this.filter = Bloom.optimalFilter(1000, 0.01)
    for (const name of Array.from(this.all)) {
      this.filter.add(name)
    }
  }

  private numCachedPages(): number {
    return this.cached.length / this.pageSize
  }

  private popCachedPage(): string[] {
    if (this.cached.length < this.pageSize) {
      throw new Error('Not enough cached names')
    }

    const names: string[] = []

    for (let i = 0; i < this.pageSize; i++) {
      const name = this.cached.shift() as string
      names.push(name)
    }

    return names
  }

  private serializeFilter(): string | undefined {
    return this.filter && Bloom.toBase64(this.filter)
  }

}


// WEBPACK FOOTER //
// ./src/service/search.ts